// iter 381a — "Waiting on Parts" dispatcher alert popup.
// Jake: "giving a pop-up for the dispatcher would be good to know" when a
// shop tech marks a build Waiting on Parts. Instant sonner toast via the
// `shop.waiting_on_parts` WS event + a persistent dialog (backed by
// shop_alerts) so nothing is missed while logged out.
import { useEffect, useState, useCallback } from "react";
import { hasAnyRole } from "../lib/roles";
import axios from "axios";
import { toast } from "sonner";
import { PackageSearch, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShopWaitingPartsAlertPopup = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchPending = useCallback(() => {
    axios.get(`${API}/shop/alerts/pending`)
      .then((res) => {
        const list = res.data?.items || [];
        setItems(list);
        if (list.length > 0) setOpen(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!hasAnyRole(["admin", "dispatcher", "manager"])) return;
    fetchPending();
    const onWs = (e) => {
      const evt = e?.detail?.event;
      if (evt !== "shop.waiting_on_parts") return;
      const d = e.detail.data || {};
      toast.warning(`${d.tech_name || "A tech"} marked ${d.customer_name || "a cart"} Waiting on Parts`, {
        description: d.note ? `Waiting on: ${d.note}` : "Check the build sheet to see what's left unchecked",
        duration: 12000,
      });
      fetchPending();
    };
    window.addEventListener("ws_event", onWs);
    return () => window.removeEventListener("ws_event", onWs);
  }, [fetchPending]);

  if (!open || items.length === 0) return null;

  const ack = (id) => {
    axios.post(`${API}/shop/alerts/${id}/ack`).catch(() => {});
    setItems((prev) => {
      const next = prev.filter((i) => i.id !== id);
      if (next.length === 0) setOpen(false);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-[#111113] border-white/10 text-white" data-testid="shop-alert-popup">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <PackageSearch className="h-4 w-4 text-amber-400" />
            Waiting on Parts
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.map((it) => (
            <div key={it.id} className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`shop-alert-item-${it.id}`}>
              <div className="flex items-start gap-2">
                <Wrench className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {it.customer_name || "Unknown customer"}
                    {it.year_make_model ? (
                      <span className="text-zinc-400 font-normal ml-1.5">· {it.year_make_model}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    {it.tech_name || "A tech"} marked this build Waiting on Parts — status updated in Airtable
                  </div>
                  {it.note ? (
                    <div className="text-xs text-amber-300/90 mt-1" data-testid={`shop-alert-note-${it.id}`}>
                      Waiting on: {it.note}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs border-white/15 bg-transparent text-zinc-200 hover:bg-white/10"
                  onClick={() => ack(it.id)} data-testid={`shop-alert-ack-${it.id}`}>
                  Got it
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
